/**
 * Tone presets — the small, contrast-first vocabulary behind the onboarding
 * "which one sounds like you?" step and the post inspector's tone control.
 *
 * WHY NOT `CAPTION_TONES` (friendly / professional / casual)? Those three
 * were written before a brand voice existed anywhere near this surface, and
 * they collapse against a real one: a brand whose scanned tone is already
 * "warm, calm, quietly confident" gets a near-duplicate "Friendly" card and is
 * asked to tell two identical things apart. These three are chosen so that any
 * two of them read as visibly different captions on the same base text —
 * which is the entire point of picking a voice by example rather than by name.
 *
 * `voiceClause` is the shared phrase behind BOTH the full rewrite instruction
 * and the short clause appended to other caption edits, so a tone can never
 * mean one thing to "Rewrite in this tone" and a different thing to "Shorten".
 */

export type TonePresetId = 'warm' | 'professional' | 'bold';

/**
 * The id the brand's own voice answers to inside the tone control. It is a UI
 * value only: `social_posts.tone_override` stores NULL for it, so "fall back
 * to the brand tone" is structural rather than a convention someone forgets.
 */
export const BRAND_TONE_OPTION_ID = 'brand';

export interface TonePreset {
  id: TonePresetId;
  label: string;
  blurb: string;
  /** The phrase the model is given. Shared by the rewrite and the keep-clause. */
  voiceClause: string;
  /**
   * What lands in `user_brand_voices.tone` when this preset is chosen during
   * onboarding. Deliberately NOT the label ("Bold & direct" reads as a UI
   * string in the Voice field and in the agent's system prompt) and not the
   * full `voiceClause` (an em-dashed sentence reads as machinery). This is the
   * form a person would have typed into that field themselves.
   */
  toneText: string;
}

export const TONE_PRESETS: ReadonlyArray<TonePreset> = [
  {
    id: 'warm',
    label: 'Warm & personal',
    blurb: 'Sounds like a person, not a brand.',
    voiceClause:
      'warm and personal — it sounds like one person talking to another, not a brand announcing something',
    toneText: 'Warm, personal, conversational',
  },
  {
    id: 'professional',
    label: 'Polished & professional',
    blurb: 'Clear, credible, no slang.',
    voiceClause: 'polished and professional — clear, credible, specific, and free of slang',
    toneText: 'Polished, professional, precise',
  },
  {
    id: 'bold',
    label: 'Bold & direct',
    blurb: 'Short sentences. Strong claims.',
    voiceClause: 'bold and direct — short sentences, strong claims, no hedging',
    toneText: 'Bold, direct, no hedging',
  },
];

const PRESET_BY_ID = new Map<TonePresetId, TonePreset>(
  TONE_PRESETS.map((preset) => [preset.id, preset]),
);

export function isTonePresetId(value: unknown): value is TonePresetId {
  return typeof value === 'string' && PRESET_BY_ID.has(value as TonePresetId);
}

export function tonePresetById(value: string | null | undefined): TonePreset | null {
  if (!isTonePresetId(value)) return null;
  return PRESET_BY_ID.get(value) ?? null;
}

/**
 * Words that make a scanned brand tone read as ALREADY being one of the
 * presets. Used only to decide which two presets to show ALONGSIDE the brand's
 * own voice in onboarding — never to rewrite or reinterpret the brand tone
 * itself, which is always sent to the model verbatim.
 */
const PRESET_KEYWORDS: Record<TonePresetId, ReadonlyArray<string>> = {
  warm: [
    'warm',
    'friendly',
    'personal',
    'kind',
    'caring',
    'playful',
    'casual',
    'approachable',
    'calm',
    'gentle',
    'human',
    'conversational',
    'quirky',
    'fun',
    'inviting',
    'nurturing',
  ],
  professional: [
    'professional',
    'polished',
    'precise',
    'considered',
    'expert',
    'credible',
    'authoritative',
    'refined',
    'formal',
    'trusted',
    'clear',
    'clean',
    'sophisticated',
    'elegant',
    'premium',
    'informative',
  ],
  bold: [
    'bold',
    'direct',
    'punchy',
    'no-nonsense',
    'nononsense',
    'confident',
    'strong',
    'energetic',
    'fearless',
    'edgy',
    'empowering',
    'blunt',
    'decisive',
    'unapologetic',
    'daring',
  ],
};

/**
 * The preset a scanned brand tone most resembles, or null when nothing in it
 * is recognisable. Deliberately dumb keyword counting: a model call to answer
 * "which of three words is this closest to" would be a paid request for a
 * cosmetic ordering decision, and a wrong answer here costs the user nothing
 * worse than a slightly less contrasting third card.
 */
export function closestPresetFor(brandTone: string | null | undefined): TonePresetId | null {
  const normalized = (brandTone ?? '').toLowerCase();
  if (!normalized.trim()) return null;
  let best: TonePresetId | null = null;
  let bestScore = 0;
  for (const preset of TONE_PRESETS) {
    const score = PRESET_KEYWORDS[preset.id].reduce(
      (total, keyword) => (normalized.includes(keyword) ? total + 1 : total),
      0,
    );
    if (score > bestScore) {
      bestScore = score;
      best = preset.id;
    }
  }
  return best;
}

/**
 * The presets to show beside the brand's own voice. Drops whichever preset the
 * brand tone already IS, so the user is never asked to choose between two
 * cards that say the same thing in the same voice. With no brand tone, all
 * three are offered and nothing is pre-selected.
 */
export function contrastPresetsFor(brandTone: string | null | undefined): TonePreset[] {
  const closest = closestPresetFor(brandTone);
  if (!closest) return [...TONE_PRESETS];
  return TONE_PRESETS.filter((preset) => preset.id !== closest);
}
